import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  IconCircleCheck,
  IconAlertTriangle,
  IconArrowsExchange,
  IconCode,
  IconEye,
} from '@tabler/icons-react';
import type { Node } from 'reactflow';
import type { CardSurvey } from '../types';
import type { SurveyQuestionTemplate } from '../types';
import type {
  MigrationComparisonResult,
  SurveySummary,
  PathSummary,
} from '../utils/surveyMigrationCompare';
import { convertFlowToSurveyFormat, ensureStartEndNodes, generateFlowFromTemplate } from '../utils';
import { flowToCard } from '../utils/cardConverter';
import { SurveyPreview } from './SurveyPreview';

interface FlowPayload {
  nodes: unknown[];
  edges: unknown[];
}

function getOldFormatJsonForDisplay(oldPayload: unknown): unknown {
  if (oldPayload == null) return oldPayload;
  const p = oldPayload as Record<string, unknown>;
  if (typeof p === 'object' && Array.isArray(p.nodes) && Array.isArray(p.edges)) {
    const flow = p as unknown as FlowPayload;
    const mapped = flow.nodes.map((n: unknown) => {
      const node = n as Record<string, unknown>;
      return (node.id !== 'start' && node.id !== 'end' && (node.type === 'default' || !node.type))
        ? { ...node, type: 'question' }
        : node;
    });
    const nodes = ensureStartEndNodes(mapped as Node[]);
    return convertFlowToSurveyFormat(nodes, flow.edges as Parameters<typeof convertFlowToSurveyFormat>[1]);
  }
  if (typeof p === 'object' && 'survey' in p && Array.isArray(p.survey)) {
    return p.survey;
  }
  if (Array.isArray(oldPayload)) {
    return oldPayload;
  }
  return oldPayload;
}

function oldFormatDisplayToCardSurvey(displayOldJson: unknown): CardSurvey | null {
  if (!Array.isArray(displayOldJson) || displayOldJson.length === 0) return null;
  try {
    const flowData = generateFlowFromTemplate(displayOldJson as SurveyQuestionTemplate[]);
    const nodesWithStartEnd = ensureStartEndNodes(flowData.nodes);
    return flowToCard(nodesWithStartEnd, flowData.edges);
  } catch (e) {
    console.warn('oldFormatDisplayToCardSurvey failed', e);
    return null;
  }
}

export interface MigrationComparisonViewProps {
  oldPayload: unknown;
  convertedCardSurvey: CardSurvey;
  comparison: MigrationComparisonResult;
  onConfirmUseNewFormat: () => void;
  onCancel?: () => void;
}

function SummaryPanel({
  title,
  summary,
  side,
}: {
  title: string;
  summary: SurveySummary;
  side: 'old' | 'new';
}) {
  return (
    <div className={cn(
      'h-full rounded border p-2',
      side === 'old' ? 'bg-muted/50' : 'bg-primary/5'
    )}>
      <h6 className="mb-1 text-base font-bold">{title}</h6>
      <p className="mb-1 block text-xs text-muted-foreground">
        {summary.questionIds.length} question(s), {summary.paths.length} path(s)
      </p>
      <ul className="space-y-0.5">
        {summary.questionIds.map((id) => {
          const q = summary.questions[id];
          if (!q) return null;
          const opts = q.options?.length ? ` [${q.options.join(', ')}]` : '';
          const pathNote = summary.paths.some(
            (p) => p.questionIds.includes(id)
          )
            ? ' (path)'
            : '';
          return (
            <li key={id} className="py-0.5">
              <p className="text-sm">
                Q{q.number}: {(q.text || '').slice(0, 50)}{(q.text || '').length > 50 ? '…' : ''}
              </p>
              <p className="text-xs text-muted-foreground">
                {q.type}{opts}{pathNote}
              </p>
            </li>
          );
        })}
      </ul>
      {summary.paths.length > 0 && (
        <>
          <Separator className="my-1" />
          <p className="text-xs font-medium text-muted-foreground">Paths</p>
          {summary.paths.map((p: PathSummary, i: number) => (
            <p key={i} className="block pl-1 text-xs">
              Q{p.triggerQuestionId} "{p.option}" → {p.questionIds.join(', ')}
            </p>
          ))}
        </>
      )}
    </div>
  );
}

export function MigrationComparisonView({
  oldPayload,
  convertedCardSurvey,
  comparison,
  onConfirmUseNewFormat,
  onCancel,
}: MigrationComparisonViewProps) {
  const {
    match,
    oldSummary,
    newSummary,
    missingInNew,
    addedInNew,
    missingPathsInNew,
    addedPathsInNew,
    differences,
  } = comparison;

  const displayOldJson = useMemo(() => getOldFormatJsonForDisplay(oldPayload), [oldPayload]);

  const hasIssues =
    missingInNew.length > 0 ||
    missingPathsInNew.length > 0 ||
    differences.length > 0;

  return (
    <div className="max-w-full overflow-hidden rounded border p-3">
      <div className="mb-2 flex items-center gap-1">
        <IconArrowsExchange className="size-5 text-muted-foreground" />
        <h6 className="text-lg font-semibold">Survey format migration</h6>
      </div>
      <p className="mb-3 text-sm text-muted-foreground">
        This survey was stored in an older format (nodes/edges). We converted it to the new card
        format for editing. Please confirm below that everything from the old format is present in
        the new format before continuing.
      </p>

      <div className="mb-2 grid grid-cols-2 gap-2">
        <SummaryPanel title="Old format (stored)" summary={oldSummary} side="old" />
        <SummaryPanel title="New format (converted)" summary={newSummary} side="new" />
      </div>

      <div className={cn(
        'rounded border p-2',
        match ? 'bg-emerald-50 dark:bg-emerald-950/20' : hasIssues ? 'bg-red-50 dark:bg-red-950/20' : 'bg-muted/30'
      )}>
        <h6 className="mb-1 text-sm font-semibold">Comparison result</h6>
        {match ? (
          <div className="flex items-center gap-1">
            <IconCircleCheck className="size-4 text-emerald-600" />
            <p className="text-sm text-emerald-700 dark:text-emerald-400">
              All content from the old format is present in the new format. Safe to continue.
            </p>
          </div>
        ) : (
          <div>
            {hasIssues && (
              <div className="mb-1 flex items-center gap-1">
                <IconAlertTriangle className="size-4 text-amber-600" />
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Some items differ or are missing in the converted format. Review before continuing.
                </p>
              </div>
            )}
            {missingInNew.length > 0 && (
              <div className="mb-1">
                <span className="text-xs font-medium text-destructive">
                  Missing in new (question IDs):{' '}
                </span>
                {missingInNew.map((id) => (
                  <Badge key={id} variant="outline" className="mr-0.5 mt-0.5 border-destructive text-destructive">
                    {id}
                  </Badge>
                ))}
              </div>
            )}
            {missingPathsInNew.length > 0 && (
              <div className="mb-1">
                <span className="text-xs font-medium text-destructive">
                  Paths in old not fully in new:{' '}
                </span>
                {missingPathsInNew.map((p, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="mr-0.5 mt-0.5 border-destructive text-destructive"
                  >
                    Q{p.triggerQuestionId} "{p.option}" → {p.questionIds.join(', ')}
                  </Badge>
                ))}
              </div>
            )}
            {differences.length > 0 && (
              <div className="mb-1">
                <span className="text-xs font-medium text-muted-foreground">
                  Field differences:{' '}
                </span>
                <ul className="mt-0.5 space-y-0">
                  {differences.slice(0, 10).map((d, i) => (
                    <li key={i} className="py-0">
                      <p className="text-xs">{d.questionId}.{d.field}</p>
                      <p className="text-xs text-muted-foreground">
                        "{d.oldValue ?? ''}" → "{d.newValue ?? ''}"
                      </p>
                    </li>
                  ))}
                  {differences.length > 10 && (
                    <p className="text-xs text-muted-foreground">
                      … and {differences.length - 10} more
                    </p>
                  )}
                </ul>
              </div>
            )}
            {addedInNew.length > 0 && !hasIssues && (
              <p className="text-xs text-muted-foreground">
                Added in new (informational): {addedInNew.join(', ')}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-3">
        <h6 className="mb-1 flex items-center gap-1 text-base font-bold">
          <IconCode className="size-4" />
          JSON comparison
        </h6>
        <JsonComparisonSection
          oldFormatJson={displayOldJson}
          newFormatJson={convertedCardSurvey}
        />
      </div>

      <div className="mt-3">
        <h6 className="mb-1 flex items-center gap-1 text-base font-bold">
          <IconEye className="size-4" />
          Preview
        </h6>
        <PreviewSection displayOldJson={displayOldJson} convertedCardSurvey={convertedCardSurvey} />
      </div>

      <div className="mt-3 flex gap-2">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          onClick={onConfirmUseNewFormat}
          variant={hasIssues ? 'outline' : 'default'}
          className={cn(hasIssues && 'border-amber-500 text-amber-700 hover:bg-amber-50')}
        >
          {hasIssues
            ? 'Use new format and continue anyway'
            : 'Use new format and continue editing'}
        </Button>
      </div>
    </div>
  );
}

function JsonComparisonSection({
  oldFormatJson,
  newFormatJson,
}: {
  oldFormatJson: unknown;
  newFormatJson: unknown;
}) {
  const oldJson = useMemo(() => JSON.stringify(oldFormatJson, null, 2), [oldFormatJson]);
  const newJson = useMemo(() => JSON.stringify(newFormatJson, null, 2), [newFormatJson]);

  return (
    <div className="max-w-full overflow-hidden rounded border p-2">
      <div className="grid min-w-0 grid-cols-2 gap-2">
        <div className="min-w-0 overflow-hidden">
          <p className="mb-1 block text-xs font-medium text-muted-foreground">
            Old format (legacy survey array: number, type, question, type_config, conditional, next_question, conditional_config)
          </p>
          <pre className="m-0 max-h-[360px] overflow-auto whitespace-pre-wrap break-words rounded border bg-muted/50 p-1.5 font-mono text-[11px]">
            {oldJson}
          </pre>
        </div>
        <div className="min-w-0 overflow-hidden">
          <p className="mb-1 block text-xs font-medium text-muted-foreground">
            New format (card JSON: format, questions array, paths)
          </p>
          <pre className="m-0 max-h-[360px] overflow-auto whitespace-pre-wrap break-words rounded border bg-primary/5 p-1.5 font-mono text-[11px]">
            {newJson}
          </pre>
        </div>
      </div>
    </div>
  );
}

function PreviewSection({
  displayOldJson,
  convertedCardSurvey,
}: {
  displayOldJson: unknown;
  convertedCardSurvey: CardSurvey;
}) {
  const cardFromOld = useMemo(() => oldFormatDisplayToCardSurvey(displayOldJson), [displayOldJson]);

  return (
    <div className="max-w-full overflow-hidden rounded border">
      <p className="p-2 pb-0 text-sm text-muted-foreground">
        Side-by-side step-through: both use the same preview. <strong>Left</strong> is driven by the old format (legacy
        survey array), <strong>right</strong> by the new format (card JSON). Compare to verify the conversion.
      </p>
      <div className="grid min-w-0 grid-cols-2 gap-2 p-2">
        <div className="relative z-[2] min-h-[320px] min-w-0 overflow-auto rounded border bg-background p-2">
          <p className="mb-1 block text-xs font-medium text-muted-foreground">
            Preview: Old format (legacy array → same UI)
          </p>
          <div className="min-h-[280px]">
            {cardFromOld ? (
              <SurveyPreview survey={cardFromOld} />
            ) : (
              <p className="text-sm text-muted-foreground">
                No legacy survey array to preview.
              </p>
            )}
          </div>
        </div>
        <div className="relative z-[1] min-h-[320px] min-w-0 overflow-auto rounded border bg-background p-2">
          <p className="mb-1 block text-xs font-medium text-muted-foreground">
            Preview: New format (card JSON)
          </p>
          <div className="min-h-[280px]">
            <SurveyPreview survey={convertedCardSurvey} />
          </div>
        </div>
      </div>
    </div>
  );
}
